import React, { useState, useMemo, memo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Grid, Icon, Message, Loader } from 'semantic-ui-react';
import { StatusLabels, StatusFilter } from '../../../components/statusTools/StatusIndicators';
import { PRIORITY_OPTIONS, STATUS_OPTIONS } from '../../../constants/statusConfig';
import { useCharacterManagement } from '../../hooks/core/useZoneHooks';
import '../../styles/voiceActorWorksheet.css';

/**
 * CharactersListEnh component
 * Enhanced version of CharactersList adapted from BookWorksheet
 * Displays a list of characters for a selected portfolio
 * EXACT SAME UI and functionality as BookWorksheet, just adapted for VoiceActor entities
 * @param {Object} props - Component props
 * @param {Function} props.onCharacterSelect - Callback when a character is selected
 * @param {Function} props.onDeleteCharacter - Callback when delete button is clicked
 * @returns {JSX.Element} CharactersListEnh component
 */
const CharactersListEnh = memo(({ onCharacterSelect, onDeleteCharacter }) => {
  const characterManagement = useCharacterManagement();

  // Extract data from new hook
  const {
    characters,
    loading,
    error,
    selections,
    actions
  } = characterManagement;

  // Map to old interface for compatibility
  const charactersLoading = loading ? 'loading' : 'idle';
  const charactersError = error;
  const characterId = selections.characterId;
  const portfolioId = selections.portfolioId; // portfolioId instead of bookId
  const fetchCharacters = actions.fetchCharacters;
  const setCharacterSelection = actions.selectCharacter;

  const dispatch = useDispatch();

  // Local state for filtering
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch characters when portfolioId changes
  useEffect(() => {
    if (portfolioId) {
      fetchCharacters(portfolioId); // portfolioId instead of bookId
    }
  }, [fetchCharacters, portfolioId]);

  // Handle character selection
  const handleCharacterSelect = useCallback((character) => {
    setCharacterSelection(character.uuid);
    if (onCharacterSelect) {
      onCharacterSelect(character);
    }
  }, [setCharacterSelection, onCharacterSelect]);

  // Handle delete button click
  const handleDeleteClick = useCallback((e, character) => {
    e.stopPropagation();
    if (onDeleteCharacter) {
      onDeleteCharacter(character);
    } else {
      // If no delete handler is provided, show a confirmation dialog
      if (window.confirm(`Are you sure you want to delete character "${character.name}"?`)) {
        console.log('Delete character:', character.uuid);
        // TODO: Implement character deletion
      }
    }
  }, [onDeleteCharacter]);

  // Status management helper functions (EXACT SAME as BookWorksheet)
  const getPriorityColor = (priority) => {
    if (!priority) return 'grey';

    // First try to find the priority in PRIORITY_OPTIONS
    const priorityOption = PRIORITY_OPTIONS.find(option =>
      option.value.toLowerCase() === priority.toLowerCase());

    if (priorityOption && priorityOption.color) {
      return priorityOption.color;
    }

    // Fallback to switch case for backward compatibility
    switch (priority.toLowerCase()) {
      case 'high':
        return 'red';
      case 'medium':
        return 'orange';
      case 'low':
        return 'green';
      default:
        return 'grey';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'grey';

    // First try to find the status in STATUS_OPTIONS
    const statusOption = STATUS_OPTIONS.find(option =>
      option.value.toLowerCase() === status.toLowerCase());

    if (statusOption && statusOption.color) {
      return statusOption.color;
    }

    // Fallback to switch case for backward compatibility
    switch (status.toLowerCase()) {
      case 'new':
        return 'blue';
      case 'in progress':
        return 'yellow';
      case 'review':
        return 'purple';
      case 'complete':
        return 'green';
      // Legacy status mappings
      case 'active':
        return 'blue';
      case 'draft':
        return 'grey';
      case 'archived':
        return 'black';
      case 'deleted':
        return 'red';
      default:
        return 'grey';
    }
  };

  // Apply filters (EXACT SAME as BookWorksheet)
  const filteredCharacters = useMemo(() => {
    if (!characters || characters.length === 0) {
      return [];
    }

    let filtered = [...characters];

    // Apply priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(character =>
        character.priority?.toLowerCase() === priorityFilter.toLowerCase());
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(character =>
        character.status?.toLowerCase() === statusFilter.toLowerCase());
    }

    return filtered;
  }, [characters, priorityFilter, statusFilter]);

  // Show error state
  if (charactersLoading === 'failed') {
    return (
      <Message negative>
        <Message.Header>Error Loading Characters</Message.Header>
        <p>{charactersError || 'An unknown error occurred'}</p>
      </Message>
    );
  }

  // Show empty state or prompt to select a portfolio
  if (!portfolioId) {
    return (
      <Message info>
        <Message.Header>No Portfolio Selected</Message.Header>
        <p>Please select a portfolio to view its characters.</p>
      </Message>
    );
  }

  // Show empty state for no characters
  if (!characters || characters.length === 0) {
    return (
      <Message info>
        <Message.Header>No Characters Found</Message.Header>
        <p>There are no characters in this portfolio. Click "New Character" to create one.</p>
      </Message>
    );
  }

  // Convert VoiceActor status/priority to display format (EXACT SAME as BookWorksheet)
  const mapStatusForDisplay = (status) => {
    // First check if the status is already in the BookCreator format
    const isBookCreatorStatus = STATUS_OPTIONS.some(option =>
      option.value.toLowerCase() === status?.toLowerCase());

    if (isBookCreatorStatus) {
      return status;
    }

    // If not, map from VoiceActor format to BookCreator format
    const statusMap = {
      'active': 'In Progress',
      'draft': 'New',
      'archived': 'Complete',
      'deleted': 'Archived'
    };

    return statusMap[status?.toLowerCase()] || 'New';
  };

  const mapPriorityForDisplay = (priority) => {
    if (!priority) return 'Medium';
    // Capitalize first letter
    return priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
  };

  // Render the characters list (EXACT SAME UI as BookWorksheet)
  return (
    <div className="va-characters-list">
      {/* Filter controls */}
      <StatusFilter
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        priorityOptions={PRIORITY_OPTIONS}
        statusOptions={STATUS_OPTIONS}
      />

      {/* Character list */}
      <Grid className='va-character-grid-container'>
        <Grid.Row className='va-character-grid-container-row' style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd', padding: '10px 0' }}>
          <Grid.Column width={14}>Name</Grid.Column>
          <Grid.Column width={2}></Grid.Column>
        </Grid.Row>

        {filteredCharacters.length === 0 ? (
          <Grid.Row style={{ padding: '20px 0', textAlign: 'center' }}>
            <Grid.Column width={16}>
              <p style={{ color: '#888' }}>
                No characters match the selected filters.
              </p>
            </Grid.Column>
          </Grid.Row>
        ) : (
          filteredCharacters.map((character) => (
            <Grid.Row
              key={character.uuid}
              style={{
                padding: '10px 0',
                backgroundColor: characterId === character.uuid ? '#ebf3fb' : 'transparent',
                cursor: 'pointer',
                // borderBottom: '1px solid #eee'
              }}
              onClick={() => handleCharacterSelect(character)}
              className={characterId === character.uuid ? 'va-character-grid-container-row va-selected' : 'va-character-grid-container-row'}
            >
              <Grid.Column className="noRightPad" width={14}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}>
                  <div style={{
                    fontWeight: 'medium'
                  }}>
                    {character.name}
                  </div>
                  <StatusLabels
                    priority={mapPriorityForDisplay(character.priority) || 'Medium'}
                    status={mapStatusForDisplay(character.status) || 'New'}
                    deadline={character.deadline}
                    getPriorityColor={getPriorityColor}
                    getStatusColor={getStatusColor}
                  />
                </div>
              </Grid.Column>
              <Grid.Column width={2} textAlign="right">
                <Icon
                  name="trash alternate"
                  color="red"
                  className="va-trash-icon"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => handleDeleteClick(e, character)}
                />
              </Grid.Column>
            </Grid.Row>
          ))
        )}
      </Grid>
    </div>
  );
});

export default CharactersListEnh;
